/**
 * UpcomingOrdersWidget - Dashboard widget showing upcoming orders with their status
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, Typography, Button, Space, Tag, Empty } from "antd";
import {
  ShoppingOutlined,
  RightOutlined,
  CalendarOutlined,
  DollarOutlined,
  TruckOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import useUpcomingOrdersWidget from "../hooks/useUpcomingOrdersWidget.js";
import { formatPickupTime } from "../../../shared/utils/formatPickupTime.js";

const { Text } = Typography;

const WidgetCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: 100%;

  .ant-card-body {
    padding: 16px;
  }
`;

const OrderItem = styled.div`
  padding: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #1890ff;
    box-shadow: 0 2px 4px rgba(24, 144, 255, 0.1);
  }

  .order-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
  }

  .order-info {
    flex: 1;
  }

  .order-id {
    font-weight: 600;
    margin-bottom: 4px;
    font-family: monospace;
  }

  .order-meta {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    font-size: 12px;
    color: #8c8c8c;
    margin-top: 4px;
  }

  .order-details {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    font-size: 12px;
    margin-top: 8px;
  }

  .detail-item {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;

const StatusTag = styled(Tag)`
  font-weight: 500;
`;

const UpcomingOrdersWidget = () => {
  const navigate = useNavigate();
  const { data, loading, error } = useUpcomingOrdersWidget();

  const upcomingOrders = data?.upcomingOrders || [];

  const handleOrderClick = (orderId) => {
    navigate(`/orders/${orderId}`);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "success";
      case "in_progress":
      case "processing":
        return "processing";
      case "pending":
        return "warning";
      case "booked":
        return "cyan";
      case "arrived_at_storage":
        return "blue";
      case "in_transit_from_storage":
        return "geekblue";
      case "cancelled":
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  const formatStatus = (status) => {
    if (!status) return "Unknown";
    return status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getPickupTime = (order) => {
    return formatPickupTime(order.booking) || "Not scheduled";
  };

  const renderOrderItem = (order) => {
    return (
      <OrderItem
        key={order.id || order._id}
        onClick={() => handleOrderClick(order.id || order._id)}
      >
        <div className="order-header">
          <div className="order-info">
            <div className="order-id">{order.id || order._id}</div>
            <div className="order-meta">
              <StatusTag color={getStatusColor(order.status)}>
                {formatStatus(order.status)}
              </StatusTag>
              {order.priority && (
                <Tag color={order.priority === "urgent" ? "red" : "default"}>
                  {order.priority}
                </Tag>
              )}
              {order.customer && <Text type="secondary">{order.customer}</Text>}
            </div>
          </div>
          <RightOutlined style={{ color: "#8c8c8c", marginTop: 4 }} />
        </div>

        <div className="order-details">
          <div className="detail-item">
            <CalendarOutlined />
            <Text type="secondary">{getPickupTime(order)}</Text>
          </div>
          {order.estimatedCost && (
            <div className="detail-item">
              <DollarOutlined />
              <Text type="secondary">
                R{order.estimatedCost.toLocaleString()}
              </Text>
            </div>
          )}
          {order.items?.length > 0 && (
            <div className="detail-item">
              <ShoppingOutlined />
              <Text type="secondary">{order.items.length} item(s)</Text>
            </div>
          )}
          {order.delivery?.address && (
            <div className="detail-item">
              <TruckOutlined />
              <Text type="secondary" ellipsis style={{ maxWidth: 200 }}>
                {order.delivery.address}
              </Text>
            </div>
          )}
        </div>
      </OrderItem>
    );
  };

  const displayedOrders = upcomingOrders.slice(0, 5);

  return (
    <WidgetCard
      title={
        <Space>
          <ShoppingOutlined />
          <span>Upcoming Orders</span>
        </Space>
      }
      extra={
        <Button type="link" size="small" onClick={() => navigate("/orders")}>
          View All
        </Button>
      }
      loading={loading}
    >
      {error && (
        <Empty
          description="Failed to load upcoming orders"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}

      {!loading && !error && (
        <>
          {/* Summary */}
          {upcomingOrders.length > 0 && (
            <Space size="large" style={{ marginBottom: 16 }}>
              <div>
                <Text strong>{upcomingOrders.length}</Text>
                <Text type="secondary" style={{ marginLeft: 4 }}>
                  Active Orders
                </Text>
              </div>
            </Space>
          )}

          {/* Orders List */}
          {displayedOrders.length > 0 ? (
            <div>
              {displayedOrders.map((order) => renderOrderItem(order))}
              {upcomingOrders.length > 5 && (
                <Button
                  type="link"
                  block
                  onClick={() => navigate("/orders")}
                  style={{ marginTop: 8 }}
                >
                  View {upcomingOrders.length - 5} more orders
                </Button>
              )}
            </div>
          ) : (
            <Empty
              description="No upcoming orders"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </>
      )}
    </WidgetCard>
  );
};

export default UpcomingOrdersWidget;
